/**
 * The portal's own `fetch` for calls to its API: the same as `fetch`, and when the API answers `401` it tells whoever is
 * listening that the session has gone. The session is only checked when the app loads, so without this a session that
 * expires while the app is open (or an impersonation that times out) leaves every page showing its own "401" error. The
 * session provider listens, checks the session again, and a deep link then goes to sign-in as it would on a fresh load.
 *
 * The session check itself (`GET /api/session/me`) calls `fetch` directly: its `401` is the answer, not a sign of expiry.
 */
type SessionExpiredListener = () => void;

const listeners = new Set<SessionExpiredListener>();

/**
 * The request header that marks an automatic refresh (the host's `BackgroundRefresh.HeaderName`). The server checks the session
 * on such a request but does not count it as use, so a page left open does not keep a session alive past its idle limit.
 */
export const backgroundRefreshHeader = "X-Portal-Background-Refresh";

/** True only while an automatic refresh is starting its requests; see {@link asBackgroundRefresh}. */
let startingBackgroundRefresh = false;

/**
 * Starts an automatic refresh, marking every API request it starts as one. A request is marked when it is started, so `start`
 * must start all of its requests before its first `await` (as `Promise.all` over the fetches does); a request started later
 * goes unmarked and counts as use of the session, which only keeps the session alive a little longer. The mark is cleared as
 * soon as `start` returns, before anything a person does can run.
 *
 * @param start - Starts the refresh.
 * @returns Whatever `start` returns.
 */
export function asBackgroundRefresh<T>(start: () => T): T {
  startingBackgroundRefresh = true;
  try {
    return start();
  } finally {
    startingBackgroundRefresh = false;
  }
}

/**
 * Calls back whenever an API call answers `401`.
 *
 * @param listener - Called once per `401` response.
 * @returns Stops listening.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * `fetch` for the portal's API, reporting a `401` to the session.
 *
 * @param input - What to fetch, as for `fetch`.
 * @param init - The request options, as for `fetch`.
 * @returns The response, unchanged.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  let request = init;
  if (startingBackgroundRefresh) {
    const headers = new Headers(init?.headers);
    headers.set(backgroundRefreshHeader, "1");
    request = { ...init, headers };
  }

  const response = await fetch(input, request);
  if (response.status === 401) {
    listeners.forEach((listener) => listener());
  }

  return response;
}
